import { daysBetween, expandRange, minusDays } from '../measurement/window-mechanics';
import {
  type DecodeResult,
  type Observation,
  type ObservedFacts,
  shownReference,
} from '../observation/observation';
import { type CampaignStatus, confirmRun } from './confirmation';
import { LEAD_IN_DAYS, ONGOING_MAX_LAG_DAYS } from './constants';
import { classifyDays, type Day } from './day';
import { measureRun, type RunMeasurement } from './measurement';
import { type EvidenceRun, segmentRuns } from './run';
import { alignRuns, type CampaignRun } from './wave';

/** The status of a run the ledger shows; a `permanent` run is a price cut and is not shown (CONTEXT.md, Confirmed campaign). */
export type CampaignConfirmation = Exclude<CampaignStatus, 'permanent'>;

/** A campaign run of one SKU at one vendor channel, measured. */
export type DetectedCampaign = CampaignRun &
  RunMeasurement & {
    readonly isOngoing: boolean; // CONTEXT.md, Ongoing campaign
    readonly confirmation: CampaignConfirmation; // CONTEXT.md, Confirmed campaign
    readonly startBeforeWindow: boolean; // run already under way on the axis's first day
  };

export type LedgerSku = {
  readonly ean: string;
  readonly observations: readonly DecodeResult[];
};

type LedgerInput = {
  readonly skus: readonly LedgerSku[];
  /** First visible day, YYYY-MM-DD; the ledger classifies from LEAD_IN_DAYS before it. */
  readonly dateStart: string;
  /** YYYY-MM-DD; the day ongoing status is judged against, and the axis's last day. */
  readonly today: string;
};

/** One brand's campaign facts: every day of every SKU at every online vendor channel, and the runs that are campaigns (CONTEXT.md, Campaign ledger). */
export type BrandLedger = {
  readonly axis: readonly string[];
  /** By vendor key (the vendor slug of the online channel), then by EAN. */
  readonly series: ReadonlyMap<string, ReadonlyMap<string, readonly Day[]>>;
  readonly campaigns: readonly DetectedCampaign[];
};

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDay(value: string): boolean {
  return ISO_DAY.test(value);
}

/** The SKU's days at the vendor channel, or undefined when the ledger holds no observation of it there. */
export function daysOf(
  ledger: BrandLedger,
  ean: string,
  vendorKey: string
): readonly Day[] | undefined {
  return ledger.series.get(vendorKey)?.get(ean);
}

// A day the vendor observed the SKU with a price or a shown reference (CONTEXT.md, Ongoing campaign).
function isObservedDay(observed: ObservedFacts): boolean {
  const { price } = observed;
  const reference = shownReference(observed);
  return (price !== null && price > 0) || (reference !== null && reference > 0);
}

function latestObservedDay(dayMap: ReadonlyMap<string, Observation>): string | null {
  let latest: string | null = null;
  for (const [day, obs] of dayMap) {
    if (isObservedDay(obs.observed) && (latest === null || day > latest)) latest = day;
  }
  return latest;
}

// Online observations by vendor key, then by day; rollup rows decode to the aggregate channel and fall out here.
function dayMapsByVendor(
  observations: readonly DecodeResult[]
): Map<string, Map<string, Observation>> {
  const byVendor = new Map<string, Map<string, Observation>>();
  for (const result of observations) {
    if (result.kind !== 'ok') continue;
    const obs = result.observation;
    if (obs.channel !== 'online') continue;
    let dayMap = byVendor.get(obs.vendor);
    if (!dayMap) {
      dayMap = new Map();
      byVendor.set(obs.vendor, dayMap);
    }
    dayMap.set(obs.day, obs);
  }
  return byVendor;
}

type SkuSeries = {
  readonly days: Day[];
  readonly runs: readonly EvidenceRun[];
  readonly lastObserved: string | null;
};

function emptyLedger(): BrandLedger {
  return { axis: [], series: new Map(), campaigns: [] };
}

/**
 * Runs the stages once over a brand's SKUs and writes each run onto its days.
 *
 * Observations are the decoded output of decodePayl. The axis ends at today, not at the visible
 * range's end, so a run past the range still gets its return check; a reader filters to its range.
 */
export function buildLedger({ skus, dateStart, today }: LedgerInput): BrandLedger {
  if (!isIsoDay(dateStart) || !isIsoDay(today)) return emptyLedger();
  const axis = expandRange({ from: minusDays(dateStart, LEAD_IN_DAYS), to: today });
  if (axis.length === 0) return emptyLedger();
  const indexOf = new Map(axis.map((date, i) => [date, i]));
  const indexOn = (date: string): number => {
    const index = indexOf.get(date);
    if (index === undefined) throw new RangeError(`run date ${date} is off the ledger axis`);
    return index;
  };

  const dayMapsBySku = skus.map(({ ean, observations }) => ({
    ean,
    byVendor: dayMapsByVendor(observations),
  }));
  const scrapedDays = new Map<string, Set<string>>();
  for (const { byVendor } of dayMapsBySku) {
    for (const [vendorKey, dayMap] of byVendor) {
      let days = scrapedDays.get(vendorKey);
      if (!days) {
        days = new Set();
        scrapedDays.set(vendorKey, days);
      }
      for (const [day, obs] of dayMap) if (isObservedDay(obs.observed)) days.add(day);
    }
  }

  const seriesByVendor = new Map<string, Map<string, SkuSeries>>();
  for (const { ean, byVendor } of dayMapsBySku) {
    for (const [vendorKey, dayMap] of byVendor) {
      const days = classifyDays(axis, dayMap, scrapedDays.get(vendorKey) ?? new Set());
      let byEan = seriesByVendor.get(vendorKey);
      if (!byEan) {
        byEan = new Map();
        seriesByVendor.set(vendorKey, byEan);
      }
      byEan.set(ean, {
        days,
        runs: segmentRuns(days, ean, vendorKey),
        lastObserved: latestObservedDay(dayMap),
      });
    }
  }

  const campaigns: DetectedCampaign[] = [];
  const series = new Map<string, Map<string, readonly Day[]>>();
  for (const [vendorKey, byEan] of seriesByVendor) {
    for (const run of alignRuns(byEan)) {
      const sku = byEan.get(run.ean);
      if (!sku) continue;
      const { days, lastObserved } = sku;
      const status = confirmRun(days, run, today, lastObserved);
      // Runs arrive in start order; a day two snapped runs both claim keeps the earlier run.
      const campaign = { run, status };
      for (let i = indexOn(run.startDate); i <= indexOn(run.endDate); i++) {
        if (days[i].campaign === null) days[i] = { ...days[i], campaign };
      }
      if (status === 'permanent') continue;
      const isFresh =
        lastObserved !== null && daysBetween(lastObserved, today) <= ONGOING_MAX_LAG_DAYS;
      campaigns.push({
        ...run,
        ...measureRun(days, run),
        isOngoing: isFresh && run.observedEndDate === lastObserved,
        confirmation: status,
        startBeforeWindow: run.startDate === axis[0],
      });
    }
    series.set(vendorKey, new Map([...byEan].map(([ean, sku]) => [ean, sku.days])));
  }
  return { axis, series, campaigns };
}
